import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AutoFixHighOutlinedIcon from '@mui/icons-material/AutoFixHighOutlined';
import { suggestTableGridLayouts } from './lib/geometry';
import {
  DialogHeader,
  FieldLabel,
  FP,
  fpDialogActionsSx,
  fpDialogPaperProps,
} from './lib/ui';

const PATTERN_LABELS = {
  grid: 'Grid',
  rows: 'Rows',
  circle: 'Circle',
  wall: 'Along wall',
};

const ArrangeSheet = ({
  open,
  onClose,
  tableCount = 0,
  onArrangeAuto,
  onArrangeGrid,
  onApplyArrangement,
}) => {
  const [rows, setRows] = useState('');
  const [cols, setCols] = useState('');

  const packs = useMemo(
    () => suggestTableGridLayouts(tableCount).slice(0, 6),
    [tableCount]
  );

  const outlinedChipSx = {
    textTransform: 'none',
    fontWeight: 700,
    borderColor: FP.line,
    color: FP.ink,
    '&:hover': { borderColor: FP.primary, bgcolor: FP.primarySoft },
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={fpDialogPaperProps}
    >
      <DialogHeader
        title="Arrange tables"
        subtitle={`Pack ${tableCount} table${
          tableCount === 1 ? '' : 's'
        } inside the hall — it may grow to keep table sizes.`}
        onClose={onClose}
      />
      <DialogContent sx={{ px: 3 }}>
        <Stack spacing={2.5} sx={{ pt: 0.5 }}>
          <Button
            variant="contained"
            startIcon={<AutoFixHighOutlinedIcon />}
            onClick={() => {
              onArrangeAuto();
              onClose();
            }}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              py: 1.1,
              bgcolor: FP.primary,
              boxShadow: '0 6px 16px rgba(45,127,249,0.30)',
              '&:hover': { bgcolor: FP.primaryDark },
            }}
          >
            Auto arrange
          </Button>

          {packs.length ? (
            <Box>
              <FieldLabel>Grid packs</FieldLabel>
              <Stack
                direction="row"
                spacing={1}
                useFlexGap
                sx={{ mt: 1, flexWrap: 'wrap' }}
              >
                {packs.map((pack) => (
                  <Button
                    key={pack.label}
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      onArrangeGrid({ rows: pack.rows, cols: pack.cols });
                      onClose();
                    }}
                    sx={outlinedChipSx}
                  >
                    {pack.label}
                  </Button>
                ))}
              </Stack>
            </Box>
          ) : null}

          <Box>
            <FieldLabel>Custom grid</FieldLabel>
            <Stack
              direction="row"
              spacing={1}
              sx={{ mt: 1 }}
              alignItems="center"
            >
              <TextField
                size="small"
                label="Rows"
                type="number"
                value={rows}
                onChange={(e) => setRows(e.target.value)}
                sx={{ width: 100 }}
              />
              <Typography sx={{ color: FP.faint, fontWeight: 700 }}>×</Typography>
              <TextField
                size="small"
                label="Cols"
                type="number"
                value={cols}
                onChange={(e) => setCols(e.target.value)}
                sx={{ width: 100 }}
              />
              <Button
                variant="outlined"
                disabled={!rows && !cols}
                onClick={() => {
                  onArrangeGrid({
                    rows: rows ? Number(rows) : undefined,
                    cols: cols ? Number(cols) : undefined,
                  });
                  onClose();
                }}
                sx={{ textTransform: 'none', fontWeight: 700 }}
              >
                Apply
              </Button>
            </Stack>
          </Box>

          <Box>
            <FieldLabel>Patterns</FieldLabel>
            <Stack
              direction="row"
              spacing={1}
              useFlexGap
              sx={{ mt: 1, flexWrap: 'wrap' }}
            >
              {['grid', 'rows', 'circle', 'wall'].map((kind) => (
                <Button
                  key={kind}
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    onApplyArrangement?.(kind);
                    onClose();
                  }}
                  sx={outlinedChipSx}
                >
                  {PATTERN_LABELS[kind] || kind}
                </Button>
              ))}
            </Stack>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={fpDialogActionsSx}>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ArrangeSheet;
